import { Injectable, Inject } from '@angular/core';
import {
  AuthorizationNotifier,
  AuthorizationRequest,
  AuthorizationServiceConfiguration,
  BaseTokenRequestHandler,
  RedirectRequestHandler,
  Requestor,
  StringMap,
  TokenRequest,
  TokenResponse,
  GRANT_TYPE_AUTHORIZATION_CODE,
  AppAuthError,
  AuthorizationServiceConfigurationJson,

} from '@openid/appauth';

import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, filter, take, combineLatest, zip } from 'rxjs/operators';
import { TokenResponseJson } from '@openid/appauth';
import { UserInfo } from './userinfo';
import { AuthorizationConfig } from './authorization_config';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { AuthenticationSettings } from '../opc/shared/models/Authenticationsettings.model';
import { ConfigService } from '../opc/shared/services/configService';
import { EnvironmentSettings } from '../opc/shared/models/environmentsettings.model';

@Injectable()
export class AuthorizationService {

  authentication: AuthenticationSettings;
  environmentSettings: EnvironmentSettings;
  private notifier = new AuthorizationNotifier();
  private authorizationHandler = new RedirectRequestHandler();

  public _tokenResponses: BehaviorSubject<TokenResponse | null>;
  private _userInfos: BehaviorSubject<UserInfo | null>;
  private _serviceConfigs: BehaviorSubject<AuthorizationServiceConfiguration | null>;
  isToken = false;
  // tokenres: any;
  isSignInLoading = false;
  get issuerUri(): string {
    return this.authentication.issuer_uri;
  }

  constructor(
    private requestor: Requestor,
    private _http: HttpClient,
    private _router: Router,
    private _configService: ConfigService,
    @Inject('AuthorizationConfig') private environment: AuthorizationConfig) {
    this.authentication = new AuthenticationSettings();
    this.environmentSettings = new EnvironmentSettings();
    this.authorizationHandler.setAuthorizationNotifier(this.notifier);
    let authorizationServiceConfiguration: AuthorizationServiceConfiguration | null = null;
    let tokenResponse: TokenResponse | null = null;
    let userInfo: UserInfo | null = null;
    const codeVerifier: string | null = null;
    if (this.authentication.issuer_uri === window.localStorage.getItem('authorization.service.issuer_uri')) {
      const serviceConfigJSON = JSON.parse(
        window.localStorage.getItem('authorization.service.parsed_openid_configuration')!);
      authorizationServiceConfiguration = serviceConfigJSON &&
        new AuthorizationServiceConfiguration(serviceConfigJSON);

      const tokenResponseJSON = JSON.parse(window.localStorage.getItem('authorization.service.token_response')!);
      tokenResponse = tokenResponseJSON && new TokenResponse(tokenResponseJSON);

      userInfo = JSON.parse(window.localStorage.getItem('authorization.service.user_info')!);
    } else {
      window.localStorage.setItem('authorization.service.issuer_uri', this.authentication.issuer_uri);
      window.localStorage.removeItem('authorization.service.parsed_openid_configuration');
      window.localStorage.removeItem('authorization.service.user_info');
      window.localStorage.removeItem('authorization.service.token_response');
      localStorage.removeItem('authorization.service.isToken');
    }

    this._tokenResponses = new BehaviorSubject(tokenResponse);
    this._serviceConfigs = new BehaviorSubject(authorizationServiceConfiguration);
    this._userInfos = new BehaviorSubject(userInfo);

    this._serviceConfigs.subscribe((config: AuthorizationServiceConfiguration | null) => {
      if (config) {
        window.localStorage.setItem('authorization.service.parsed_openid_configuration', JSON.stringify(config.toJson()));
      } else {
        window.localStorage.removeItem('authorization.service.parsed_openid_configuration');
      }
    });
    if (!this._tokenResponses) {
      this._tokenResponses.subscribe((token: TokenResponse | null) => {
        if (token) {

          localStorage.setItem('authorization.service.token_response', JSON.stringify(token.toJson()));

        } else {
          localStorage.removeItem('authorization.service.token_response');
        }
      });
    }
    this._userInfos.subscribe((info: UserInfo | null) => {
      if (info) {
        window.localStorage.setItem('authorization.service.user_info', JSON.stringify(info));
      } else {
        window.localStorage.removeItem('authorization.service.user_info');
      }
    });
    // monitor changes in metadata/tokens to possibly clear dependent values,
    // and to fetch userInfo.
    Observable.zip(this._serviceConfigs, this._tokenResponses)
      .subscribe(
        ([configuration, token]: [AuthorizationServiceConfiguration | null, TokenResponse | null]) => {

          if (configuration == null) {
            if (token != null) {
              this._tokenResponses.next(null);
            }

            this._userInfos.next(null);
            return;
          }

          if (token == null) {
            this._userInfos.next(null);
            return;
          }

          if (configuration.userInfoEndpoint == null) {
            this._userInfos.next(null);
            return;
          }
          if (token.accessToken) {
            localStorage.setItem('authorization.service.isToken', 'true');
          } else {
            localStorage.removeItem('authorization.service.isToken');
          }
        });

    this.fetchServiceConfiguration(environment);
  }

  public serviceConfiguration(): Observable<AuthorizationServiceConfiguration | null> {
    return this._serviceConfigs.asObservable().pipe(distinctUntilChanged());
  }

  public tokenResponse(): Observable<TokenResponse | null> {
    return this._tokenResponses.asObservable().pipe(distinctUntilChanged());
  }

  public userInfos(): Observable<UserInfo | null> {
    return this._userInfos.asObservable().pipe(distinctUntilChanged());
  }

  public authorize(): void {
    this._serviceConfigs
      .pipe(filter((value: any) => value != null))
      .pipe(take(1))
      .subscribe((configuration: AuthorizationServiceConfiguration) => {
        const scope = this.environment.scope || 'openid';

        const request = new AuthorizationRequest({
          client_id: this.authentication.client_id,
          redirect_uri: this.authentication.redirect_uri,
          scope: scope,
          response_type: AuthorizationRequest.RESPONSE_TYPE_CODE,
          extras: this.environment.extras
        });

        this.authorizationHandler.performAuthorizationRequest(configuration, request);
        localStorage.setItem('isSignInLoading', 'true');
      });
  }

  signOut(): void {
    this._tokenResponses.next(null);
    let logoutURL = JSON.parse(localStorage.getItem('authorization.service.parsed_openid_configuration'))['end_session_endpoint'] + '?post_logout_redirect_uri=' + this.authentication.redirect_uri;;
    window.localStorage.clear();
    //this._configService.loadSettings();
    window.location.href = logoutURL;
  }

  completeAuthorizationRequest(): Promise<TokenResponse> {
    return new Promise((resolve, reject) => {
      this._serviceConfigs
        .pipe(filter((value: any) => value != null))
        .pipe(take(1))
        .subscribe((configuration: AuthorizationServiceConfiguration) => {
          this.notifier.setAuthorizationListener((request, response, error) => {
            if (response && response.code) {
              const tokenHandler = new BaseTokenRequestHandler(this.requestor);

              // use the code to make the token request.
              const extras: StringMap = {};

              if (request.internal) {
                extras['code_verifier'] = request.internal['code_verifier'];
              }

              let tokenRequest = {};

              tokenRequest["Code"] = response.code;
              tokenRequest["CodeVerifier"] = request.internal['code_verifier'];
              return this._http.post(this.environmentSettings.apiEndPoint + 'api/auth/ValidateCodeChallenge', tokenRequest)
                .toPromise()
                .then(res => {
                  const tokenRes = new TokenResponse({
                    access_token: res["access_token"],
                    token_type: res["token_type"],
                    expires_in: res["expires_in"],
                    id_token: res["id_token"],
                    scope: res["scope"]
                  });


                  localStorage.setItem('authorization.service.isToken', 'true');
                  localStorage.setItem('app_token', JSON.stringify(res));
                  localStorage.setItem('isAdmin', res["isAdmin"]);
                  localStorage.setItem('userEmail', res["userEmail"]);
                  this._tokenResponses.next(tokenRes);
                  resolve(tokenRes);
                  return res;
                })
                .catch(
                  error => {
                    return error;
                  }
                );

            } else {
              reject(error);
            }
          });
          this.authorizationHandler.completeAuthorizationRequestIfPossible();
        }, reject);
    });
  }

  private async fetchServiceConfiguration(configuration: AuthorizationConfig) {
    const response = await AuthorizationServiceConfiguration.fetchFromIssuer(this.authentication.issuer_uri, this.requestor);
    this._serviceConfigs.next(response);
  }
}



// WEBPACK FOOTER //
// ./src/app/authorization.service.ts